import api from "../proxy/config";
import axios from "./axiosConfig";
import { buildAttachmentFields } from "./ticketAttachmentUtils";
import { mapTicketItem } from "./adminTicketUtils";
import {
  parseAdminTicketAttachmentResponse,
  parseAdminTicketListResponse,
  parseAdminTicketMessageListResponse,
  parseAdminTicketMessageSendResponse,
  parseAdminTicketSummaryResponse,
  parseAdminTicketMessagePollResponse,
  validateAdminTicketAssignRequest,
  validateAdminTicketAttachmentRequest,
  validateAdminTicketIdRequest,
  validateAdminTicketListRequest,
  validateAdminTicketMessageEditRequest,
  validateAdminTicketMessageListRequest,
  validateAdminTicketMessagePollRequest,
  validateAdminTicketMessageSendRequest,
} from "./schemas/adminTicketSchemas";

const getHeaders = () => ({
  Authorization: `Bearer ${JSON.parse(sessionStorage.getItem("token"))}`,
});

function toNumberOrZero(value) {
  return value == null ? 0 : Number(value);
}

function normalizeListBody(body) {
  if (!body || typeof body !== "object") return {};
  return {
    ...body,
    totalCount: toNumberOrZero(body.totalCount),
    page: toNumberOrZero(body.page),
    size: toNumberOrZero(body.size),
    items: Array.isArray(body.items) ? body.items : [],
  };
}

const adminTicketService = {
  listTickets: async (data) => {
    const payload = validateAdminTicketListRequest(data);
    const response = await axios.post(api.vendorTicketList, payload, { headers: getHeaders() });
    const parsed = parseAdminTicketListResponse(normalizeListBody(response?.data));
    return { ...parsed, items: parsed.items.map(mapTicketItem) };
  },

  assignTicket: async (data) => {
    const payload = validateAdminTicketAssignRequest(data);
    return axios.post(api.vendorTicketAssign, payload, { headers: getHeaders() });
  },

  closeTicket: async (data) => {
    const payload = validateAdminTicketIdRequest(data);
    return axios.post(api.vendorTicketClose, payload, { headers: getHeaders() });
  },

  reopenTicket: async (data) => {
    const payload = validateAdminTicketIdRequest(data);
    return axios.post(api.vendorTicketReopen, payload, { headers: getHeaders() });
  },

  sendMessage: async (data) => {
    const validated = validateAdminTicketMessageSendRequest(data);
    const attachment = await buildAttachmentFields(validated.file);
    const response = await axios.post(
      api.vendorTicketMessage,
      {
        ticketId: validated.ticketId,
        message: validated.message?.trim() || undefined,
        ...attachment,
      },
      { headers: getHeaders() }
    );
    return parseAdminTicketMessageSendResponse(response?.data);
  },

  listMessages: async (data) => {
    const payload = validateAdminTicketMessageListRequest(data);
    const response = await axios.post(api.vendorTicketMessageList, payload, { headers: getHeaders() });
    return parseAdminTicketMessageListResponse(normalizeListBody(response?.data));
  },

  downloadAttachment: async (data) => {
    const payload = validateAdminTicketAttachmentRequest(data);
    const response = await axios.post(api.vendorTicketMessageAttachment, payload, { headers: getHeaders() });
    return parseAdminTicketAttachmentResponse(response?.data);
  },

  editMessage: async (data) => {
    const validated = validateAdminTicketMessageEditRequest(data);
    const { file, existingAttachmentKept, ...payload } = validated;
    const attachment = await buildAttachmentFields(file);
    return axios.post(
      api.vendorTicketMessageEdit,
      {
        ticketId: payload.ticketId,
        messageId: payload.messageId,
        action: payload.action,
        message: payload.message?.trim() || undefined,
        removeAttachment: payload.removeAttachment || undefined,
        ...attachment,
      },
      { headers: getHeaders() }
    );
  },

  getSummary: async () => {
    const response = await axios.post(api.vendorTicketSummary, {}, { headers: getHeaders() });
    return parseAdminTicketSummaryResponse(response?.data);
  },

  pollMessages: async (data) => {
    const payload = validateAdminTicketMessagePollRequest(data);
    const response = await axios.post(api.vendorTicketMessagePoll, payload, { headers: getHeaders() });
    return parseAdminTicketMessagePollResponse(response?.data);
  },

  listAssignees: async () => {
    const response = await axios.post(api.vendorTicketAssigneesList, {}, { headers: getHeaders() });
    const body = response?.data ?? {};
    return {
      items: Array.isArray(body.items) ? body.items : [],
    };
  },
};

export default adminTicketService;
 